import axios from 'axios'
import { useAuthStore } from '../stores/authStore'
import { useChatStore, Message, MessageDebugInfo, TherapistStatus, ProactiveType, DebugAgentType, DEBUG_AGENT_ICONS, nextTempId, WsLogEntry, ServerLogEntry, ConsiliumTurn, ConsiliumPhase, ConsiliumPrompt, ConsiliumPromptButton } from '../stores/chatStore'
import type { TypingMetrics } from '../hooks/useTypingMetrics'

type MessageHandler = (message: Message) => void
type StatusHandler = (status: TherapistStatus, therapist: string) => void
type HandoffHandler = (from: string, to: string) => void
type ErrorHandler = (code: string, message: string) => void

// API base for token refresh
const API_URL = import.meta.env.VITE_API_URL || ''
const API_BASE = `${API_URL}/api/v1`

// Counter for unique WS log entry IDs
let _wsLogIdCounter = 0

class WebSocketManager {
  private ws: WebSocket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000
  private pingInterval: ReturnType<typeof setInterval> | null = null
  private lastPong: number = Date.now()
  private isRefreshing = false
  private _debugLevel: string = 'off'  // Tracks current debug level

  private onMessage?: MessageHandler
  private onStatus?: StatusHandler
  private onHandoff?: HandoffHandler
  private onError?: ErrorHandler

  // Log a WS event to the store (only in advanced mode)
  private logWsEvent(direction: 'in' | 'out', type: string, payload: unknown) {
    if (this._debugLevel !== 'advanced') return
    const raw = typeof payload === 'string' ? payload : JSON.stringify(payload)
    const entry: WsLogEntry = {
      id: `ws-${++_wsLogIdCounter}`,
      timestamp: new Date().toISOString(),
      direction,
      type,
      summary: raw.length > 120 ? raw.slice(0, 120) + '...' : raw,
      rawData: payload,
    }
    useChatStore.getState().addWsLogEntry(entry)
  }

  async connect() {
    // Prevent duplicate connections
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return
    }

    // Close any existing connection before creating new one
    if (this.ws) {
      this.ws.onclose = null // Prevent reconnect on intentional close
      this.ws.close()
      this.ws = null
    }

    let token = useAuthStore.getState().accessToken
    if (!token) {
      console.error('No auth token for WebSocket')
      return
    }

    // Check if token is about to expire (within 60 seconds) and refresh proactively
    if (this.isTokenExpiringSoon(token)) {
      const refreshed = await this.tryRefreshToken()
      if (refreshed) {
        token = useAuthStore.getState().accessToken!
      }
    }

    // Use env variable or derive from current location
    const wsBaseUrl = import.meta.env.VITE_WS_URL
    let wsUrl: string

    if (wsBaseUrl) {
      wsUrl = `${wsBaseUrl}/ws/chat`
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      wsUrl = `${protocol}//${window.location.host}/ws/chat`
    }

    this.ws = new WebSocket(wsUrl)

    this.ws.onopen = () => {
      // Send auth on connect
      // Send token as first message (not in URL — security)
      this.ws!.send(JSON.stringify({ type: 'auth', token }))
    }

    this.ws.onclose = (event) => {
      useChatStore.getState().setConnected(false)
      this.stopPing()
      if (event.code === 4001) {
        // Auth failure — try token refresh, then reconnect
        this.tryRefreshToken().then(refreshed => {
          if (refreshed) this.connect()
        }).catch(() => {
          // Token refresh failed — session expired, redirect to login
          useAuthStore.getState().logout()
          window.location.href = '/login'
        })
      } else {
        this.handleReconnect()
      }
    }

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error)
    }

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        this.logWsEvent('in', data.type || 'unknown', data)
        this.handleMessage(data)
      } catch (e) {
        console.error('Failed to parse WebSocket message:', e)
      }
    }
  }

  private handleMessage(data: {
    type: string
    message_id?: number
    content?: string
    therapist?: string
    timestamp?: string
    audio_url?: string
    video_url?: string
    video_index?: number  // Multi-circle: index of this video (0-based)
    total_videos?: number  // Multi-circle: total number of videos
    duration_seconds?: number
    from_therapist?: string
    to_therapist?: string
    code?: string
    message?: string
    text?: string
    status?: TherapistStatus
    character_text?: string  // Character-driven status text
    duration?: number  // Auto-dismiss duration in seconds (for character status)
    // Proactive fields
    proactive_id?: number
    proactive_type?: string
    buttons?: Array<{ text: string; action: string; value?: string | number }>
    // System Panel removed (DEC-052)
    debug_data?: Record<string, unknown>
    enabled?: boolean
    // Debug Info (DEC-050)
    debug_info?: MessageDebugInfo
    // Debug Event (DEC-047 SystemBubble)
    agent?: string
    icon?: string
    data?: Record<string, unknown>
    // Video analysis complete (ISSUE-002)
    transcription?: string
    emotion_detected?: string
    emotion_confidence?: number
    therapeutic_context?: string
    therapist_response?: string
    therapist_name?: string
    // Chat Features: Reactions, Edit, Delete
    reactions?: Array<{ emoji: string; count: number; reactedByMe: boolean }>
    edited_at?: string
    // Interactive Consilium (Phase 1B)
    speaker?: string
    phase?: string
    turn_number?: number
  }) {
    switch (data.type) {
      case 'auth_ok':
        this.reconnectAttempts = 0
        useChatStore.getState().setConnected(true)
        this.startPing()
        break

      case 'auth_error':
        console.error('WebSocket auth failed:', data.message)
        break

      case 'message':
        if (this.onMessage && data.content) {
          const msg: Message = {
            id: data.message_id || Date.now(),
            role: 'assistant',
            content: data.content,
            mediaType: 'text',
            status: 'sent',  // Delivered when we receive it
            createdAt: data.timestamp || new Date().toISOString(),
          }
          // DEC-050: Include debug_info if present
          if (data.debug_info) {
            msg.debugInfo = data.debug_info as MessageDebugInfo
          }
          this.onMessage(msg)
        } else {
          console.warn('[DIAG] message event SKIPPED: onMessage=', !!this.onMessage, 'content=', !!data.content)
        }
        // Update current therapist if changed (e.g., Harold during onboarding)
        if (data.therapist) {
          this.updateTherapistFromResponse(data.therapist)
        }
        useChatStore.getState().setTherapistStatus('idle')
        break

      case 'voice':
        if (this.onMessage && data.audio_url && data.message_id) {
          useChatStore.getState().updateMessage(data.message_id, {
            voiceUrl: data.audio_url,
            ...(data.duration_seconds != null && { duration: data.duration_seconds }),
          })
        } else {
          console.warn('[DIAG] voice event SKIPPED: onMessage=', !!this.onMessage, 'audio_url=', !!data.audio_url, 'message_id=', data.message_id)
        }
        break

      case 'video':
        if (this.onMessage && data.video_url && data.message_id) {
          const videoUrl = data.video_url as string
          const msgId = data.message_id as number
          // Atomic update with videoUrls array dedup
          useChatStore.setState((state) => {
            const idx = state.messages.findIndex(m => m.id === msgId && m.role === 'assistant')
            if (idx === -1) return state
            const target = state.messages[idx]
            const existingUrls = target.videoUrls || []
            if (existingUrls.includes(videoUrl)) return state // Already have this video
            const newUrls: string[] = [...existingUrls, videoUrl]
            const updatedMessages = [...state.messages]
            updatedMessages[idx] = {
              ...target,
              videoUrl: newUrls[0],
              videoUrls: newUrls,
              totalVideos: data.total_videos || 1,
              ...(data.duration_seconds != null && { duration: data.duration_seconds }),
            }
            return { messages: updatedMessages }
          })
        }
        break

      case 'typing':
        // Legacy typing event - treat as 'typing' status
        useChatStore.getState().setTherapistStatus('typing')
        if (this.onStatus && data.therapist) {
          this.onStatus('typing', data.therapist)
        }
        break

      case 'user_message_confirmed':
        // Server confirmed user message was received - update status to 'sent'
        // Don't update ID here — real ID comes with user_message_processed
        useChatStore.getState().updateLastMessageWhere(
          (m) => m.role === 'user' && m.status === 'sending',
          { status: 'sent' }
        )
        break

      case 'user_message_processed':
        // Therapist processed user message - update status to 'seen'
        // BUG-051: Also match 'sending' status in fallback — if user_message_confirmed
        // was missed or arrived out of order, message may still be 'sending'
        {
          const store = useChatStore.getState()
          // Try to find by server ID first
          const processedMsgId = data.message_id as number | undefined
          const byId = processedMsgId && store.messages.find(m => m.id === processedMsgId && m.role === 'user')
          if (byId && processedMsgId) {
            store.updateMessage(processedMsgId, { status: 'seen' })
          } else {
            // Fallback: find last user message with 'sent' or 'sending' status
            store.updateLastMessageWhere(
              (m) => m.role === 'user' && (m.status === 'sent' || m.status === 'sending'),
              { id: processedMsgId || undefined, status: 'seen' }
            )
          }
        }
        break

      case 'status':
        // New status event with different statuses
        if (data.status) {
          useChatStore.getState().setTherapistStatus(data.status)
          // Handle character status text with auto-dismiss duration
          if (data.status === 'character' && data.character_text) {
            useChatStore.getState().setCharacterStatus(data.character_text, data.duration)
          } else if (data.status === 'idle') {
            // Only idle explicitly clears character status — technical statuses
            // (typing, recording, etc.) temporarily override display but don't cancel timer
            useChatStore.getState().setCharacterStatus(null)
          }
          // Mark user message as 'seen' when therapist starts watching/listening
          if (data.status === 'watching_video' || data.status === 'listening_audio' || data.status === 'analyzing_photo') {
            const msgId = data.message_id as number | undefined
            if (msgId) {
              useChatStore.getState().updateMessage(msgId, { status: 'seen' })
            } else {
              // Fallback: find last user message with 'sent' status
              useChatStore.getState().updateLastMessageWhere(
                (m) => m.role === 'user' && m.status === 'sent',
                { status: 'seen' }
              )
            }
          }
          if (this.onStatus && data.therapist) {
            this.onStatus(data.status, data.therapist)
          }
        }
        break

      case 'handoff':
        if (this.onHandoff && data.from_therapist && data.to_therapist) {
          this.onHandoff(data.from_therapist, data.to_therapist)
        }
        // Update current therapist on handoff
        if (data.to_therapist) {
          this.updateTherapistFromResponse(data.to_therapist)
        }
        break

      case 'onboarding_complete':
        // Explicit onboarding completion event
        localStorage.setItem('onboarding_complete', 'true')
        // Trigger handoff callback if set (for UI updates)
        if (this.onHandoff) {
          this.onHandoff('finch', data.to_therapist || 'wendy')
        }
        if (data.to_therapist) {
          this.updateTherapistFromResponse(data.to_therapist)
        }
        break

      case 'transcription':
        // Voice transcribed — update bubble text + status to 'seen'
        if (data.text) {
          useChatStore.getState().updateLastMessageWhere(
            (m) => m.role === 'user',
            { content: data.text, status: 'seen' }
          )
        }
        break

      case 'error':
        if (this.onError && data.code && data.message) {
          this.onError(data.code, data.message)
        }
        break

      case 'pong':
        this.lastPong = Date.now()
        break

      case 'proactive':
        // Proactive engagement prompt from therapist
        if (data.proactive_type && data.content) {
          useChatStore.getState().setProactivePrompt({
            id: data.proactive_id || Date.now(),
            type: data.proactive_type as ProactiveType,
            message: data.content,
            therapist: data.therapist || 'wendy',
            buttons: data.buttons,
            timestamp: data.timestamp || new Date().toISOString(),
          })
        }
        break

      // system_panel removed — DEC-052: all debug data via debug_event channel

      case 'debug_mode_changed':
        // DEC-047: Debug mode toggle acknowledgment
        // Debug mode changed
        break

      case 'debug_event':
        // DEC-047: Inline debug event (SystemBubble)
        if (data.agent && data.message_id) {
          const agentType = data.agent as string
          const isKnown = agentType in DEBUG_AGENT_ICONS
          useChatStore.getState().addDebugEvent({
            id: `${data.message_id}-${agentType}-${Date.now()}`,
            agent: agentType as DebugAgentType,
            icon: data.icon || (isKnown ? DEBUG_AGENT_ICONS[agentType as DebugAgentType] : '⚙️'),
            data: data.data || {},
            timestamp: data.timestamp || new Date().toISOString(),
            messageId: data.message_id,
          })
          if (!isKnown) {
            console.warn(`Unknown debug_event agent type: "${agentType}"`)
          }
        }
        break

      case 'video_analysis_complete':
        // Background video analysis complete (ISSUE-002 fix)
        // Update USER message with analysis results and mark as 'seen'
        // BUG-047: Therapist response now arrives via separate "message" event
        // (sent from media.py before video events, with real DB ID)
        if (data.message_id) {
          useChatStore.getState().updateMessage(data.message_id, {
            content: data.content || data.transcription || undefined,
            videoUrl: data.video_url || undefined,  // BUG-049: permanent URL
            transcription: data.transcription,
            emotionDetected: data.emotion_detected,
            emotionConfidence: data.emotion_confidence,
            status: 'seen',
          })
        }
        useChatStore.getState().setTherapistStatus('idle')
        break

      case 'image_analysis_complete':
        // Background image analysis complete (Photo-524 fix)
        // Update USER message with analysis results and mark as 'seen'
        // Therapist response arrives via separate "message" event
        if (data.message_id) {
          useChatStore.getState().updateMessage(data.message_id, {
            content: data.content || undefined,
            emotionDetected: data.emotion_detected,
            emotionConfidence: data.emotion_confidence,
            status: 'seen',
          })
        }
        useChatStore.getState().setTherapistStatus('idle')
        break

      // Chat Features: Reactions, Edit, Delete
      case 'reaction_update':
        // Update message reactions
        if (data.message_id && data.reactions) {
          useChatStore.getState().updateMessageReactions(
            data.message_id,
            data.reactions as Array<{ emoji: string; count: number; reactedByMe: boolean }>
          )
        }
        break

      case 'message_edited':
        // Message was edited
        if (data.message_id && data.content && data.edited_at) {
          useChatStore.getState().markMessageEdited(
            data.message_id,
            data.content,
            data.edited_at
          )
        }
        break

      case 'message_deleted':
        // Message was deleted
        if (data.message_id) {
          useChatStore.getState().markMessageDeleted(data.message_id)
        }
        break

      case 'chat_cleared':
        // Chat was cleared
        useChatStore.getState().clearMessages()
        break

      case 'response_deleted':
        // Old therapist response deleted (during edit flow)
        if (data.message_id) {
          useChatStore.getState().markMessageDeleted(data.message_id)
        }
        break

      case 'server_log': {
        // DEC-050: Server log streaming for advanced debug
        const logEntry: ServerLogEntry = {
          id: `slog-${++_wsLogIdCounter}`,
          timestamp: (data as Record<string, unknown>).timestamp as string || new Date().toISOString(),
          level: ((data as Record<string, unknown>).level as string || 'INFO').toUpperCase() as ServerLogEntry['level'],
          logger: (data as Record<string, unknown>).logger as string || 'root',
          message: (data as Record<string, unknown>).message as string || '',
        }
        useChatStore.getState().addServerLog(logEntry)
        break
      }

      case 'server_log_backfill': {
        // Backfill: batch of buffered server logs sent on advanced debug enable
        const logs = (data as Record<string, unknown>).logs as Array<Record<string, unknown>>
        if (Array.isArray(logs)) {
          const entries: ServerLogEntry[] = logs.map(log => ({
            id: `slog-${++_wsLogIdCounter}`,
            timestamp: log.timestamp as string || new Date().toISOString(),
            level: (log.level as string || 'INFO').toUpperCase() as ServerLogEntry['level'],
            logger: log.logger as string || 'root',
            message: log.message as string || '',
          }))
          useChatStore.getState().prependServerLogs(entries)
        }
        break
      }

      // =====================================================================
      // Interactive Consilium (Phase 1B)
      // =====================================================================

      case 'consilium_phase':
        if (data.phase) {
          useChatStore.getState().setConsiliumPhase(data.phase as ConsiliumPhase)
        }
        break

      case 'consilium_turn':
        if (data.content && data.speaker) {
          const turn: ConsiliumTurn = {
            id: `ct-${data.turn_number || Date.now()}-${data.speaker}`,
            speaker: data.speaker as string,
            content: data.content as string,
            phase: (data.phase as ConsiliumPhase) || 'discussion',
            turnNumber: (data.turn_number as number) || 0,
            timestamp: data.timestamp || new Date().toISOString(),
          }
          useChatStore.getState().addConsiliumTurn(turn)
        }
        break

      case 'consilium_action_plan':
        if (data.data) {
          const plan = data.data as Record<string, unknown>
          const nc = plan.next_consilium as Record<string, unknown> | undefined
          useChatStore.getState().setConsiliumActionPlan({
            synthesis: (plan.synthesis as string) || '',
            keyInsights: (plan.key_insights as string[]) || [],
            actionSteps: (plan.action_steps as Array<{ step: string; why: string; timeline: string; priority: 'high' | 'medium' | 'low' }>) || [],
            goals: (plan.goals as Array<{ description: string; timeline: string; therapist: string }>) || [],
            focusAreas: (plan.focus_areas as string[]) || [],
            nextSessionGuidance: (plan.next_session_guidance as Record<string, string>) || {},
            nextConsilium: nc ? { recommendedIn: (nc.recommended_in as string) || '', triggers: (nc.triggers as string[]) || [] } : null,
          })
        }
        break

      case 'consilium_end':
        useChatStore.getState().endConsilium()
        break

      case 'consilium_prompt': {
        // Ephemeral prompt card (join discussion / pre-synthesis question)
        const promptType = (data as Record<string, unknown>).prompt_type as string
        const promptMessage = data.content || data.message || ''
        const promptButtons = ((data as Record<string, unknown>).buttons as Array<{ action: string; label: string }>) || []
        const consiliumPrompt: ConsiliumPrompt = {
          promptType: promptType as ConsiliumPrompt['promptType'],
          message: promptMessage as string,
          buttons: promptButtons.map((b): ConsiliumPromptButton => ({ action: b.action, label: b.label })),
        }
        useChatStore.getState().setConsiliumPrompt(consiliumPrompt)
        break
      }

      case 'consilium_prompt_dismiss':
        // Server tells us to dismiss the ephemeral prompt
        useChatStore.getState().setConsiliumPrompt(null)
        break

      case 'consilium_proposal':
        // Proactive consilium proposal — reuse proactive prompt system
        if (data.content) {
          useChatStore.getState().setProactivePrompt({
            id: data.proactive_id || Date.now(),
            type: 'consilium_proposal' as ProactiveType,
            message: data.content,
            therapist: data.therapist || 'finch',
            buttons: data.buttons || [
              { text: 'Давайте', action: 'start_consilium' },
              { text: 'Позже', action: 'dismiss' },
            ],
            timestamp: data.timestamp || new Date().toISOString(),
          })
        }
        break

      default:
        console.warn('Unknown WebSocket message type:', data.type)
    }
  }

  private async handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30_000)
      // Reconnect with exponential backoff

      // On first reconnect attempt, try to refresh the token
      if (this.reconnectAttempts === 1 && !this.isRefreshing) {
        const refreshed = await this.tryRefreshToken()
        if (!refreshed) {
          // Token refresh failed, attempting reconnect with current token
        }
      }

      setTimeout(() => this.connect(), delay)
    } else {
      console.error('Max reconnection attempts reached')
      // Try one final token refresh before giving up
      const refreshed = await this.tryRefreshToken()
      if (refreshed) {
        this.reconnectAttempts = 0
        this.connect()
      } else {
        useChatStore.getState().setConnectionFailed(true)
      }
    }
  }

  private isTokenExpiringSoon(token: string): boolean {
    try {
      // Decode JWT payload (base64)
      const payload = JSON.parse(atob(token.split('.')[1]))
      const exp = payload.exp
      if (!exp) return false

      // Check if token expires within 60 seconds
      const now = Math.floor(Date.now() / 1000)
      return exp - now < 60
    } catch {
      // If we can't decode, assume it might be expired
      return true
    }
  }

  private async tryRefreshToken(): Promise<boolean> {
    if (this.isRefreshing) return false

    const refreshToken = useAuthStore.getState().refreshToken
    if (!refreshToken) {
      // No refresh token available
      return false
    }

    this.isRefreshing = true
    try {
      // Attempting token refresh
      const response = await axios.post(`${API_BASE}/auth/refresh`, {
        refresh_token: refreshToken,
      }, { timeout: 10_000 })

      const { access_token, refresh_token } = response.data
      useAuthStore.getState().updateTokens(access_token, refresh_token)
      // Token refreshed successfully
      return true
    } catch (error) {
      console.error('Token refresh failed:', error)
      // If refresh fails, logout the user
      useAuthStore.getState().logout()
      window.location.href = '/login'
      return false
    } finally {
      this.isRefreshing = false
    }
  }

  private updateTherapistFromResponse(therapistName: string) {
    const therapistConfigs: Record<string, { name: string; avatarUrl: string }> = {
      wendy: { name: 'Венди', avatarUrl: '/avatars/wendy.png' },
      artem: { name: 'Артём', avatarUrl: '/avatars/artem.png' },
      harold: { name: 'Гарольд', avatarUrl: '/avatars/finch.png' },
      finch: { name: 'Гарольд', avatarUrl: '/avatars/finch.png' },
      una: { name: 'Уна', avatarUrl: '/avatars/una.png' },
    }

    const key = therapistName.toLowerCase()
    const config = therapistConfigs[key]
    const currentTherapist = useChatStore.getState().currentTherapist

    // Update if therapist changed OR if current is 'auto' (show real therapist)
    const shouldUpdate = config && (
      currentTherapist?.type !== key ||
      currentTherapist?.type === 'auto'
    )

    if (shouldUpdate) {
      useChatStore.getState().setTherapist({
        type: key,
        name: config.name,
        avatarUrl: config.avatarUrl,
      })
    }
  }

  // Wrapped send — logs outgoing events for advanced debug
  private wsSend(payload: Record<string, unknown>) {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    const json = JSON.stringify(payload)
    this.ws.send(json)
    this.logWsEvent('out', payload.type as string || 'unknown', payload)
  }

  private startPing() {
    this.lastPong = Date.now()
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        // Detect dead connection: no pong in 90s (3 missed cycles)
        if (Date.now() - this.lastPong > 90_000) {
          console.warn('No pong in 90s, forcing reconnect')
          this.ws.close(4000, 'Heartbeat timeout')
          return
        }
        this.ws.send(JSON.stringify({ type: 'ping' }))
      }
    }, 30000)
  }

  private stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }
  }

  disconnect() {
    this.stopPing()
    if (this.ws) {
      this.ws.onclose = null // Prevent reconnect on intentional disconnect
      this.ws.close()
      this.ws = null
    }
  }

  sendMessage(content: string, typingMetrics?: TypingMetrics) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const payload: Record<string, unknown> = { type: 'message', content, media_type: 'text' }
      if (typingMetrics) {
        payload.typing_metrics = typingMetrics
      }
      this.wsSend(payload)
      // Add user message to store immediately with 'sending' status
      useChatStore.getState().addMessage({
        id: nextTempId(),
        role: 'user',
        content,
        mediaType: 'text',
        status: 'sending',
        createdAt: new Date().toISOString(),
      })
    }
  }

  startVoiceRecording() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.wsSend({ type: 'voice_start' })
    }
  }

  sendVoiceChunk(base64Data: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      // Don't log voice chunks — too noisy, just send directly
      this.ws.send(JSON.stringify({ type: 'voice_data', data: base64Data }))
    }
  }

  endVoiceRecording(durationSeconds?: number) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.wsSend({ type: 'voice_end', duration: durationSeconds })
    }
  }

  // DEC-047/050: Set debug level (off/basic/advanced)
  setDebugLevel(level: string) {
    this._debugLevel = level
    if (this.ws?.readyState === WebSocket.OPEN) {
      const payload = {
        type: 'set_debug_mode',
        enabled: level !== 'off',  // backward compat for backend
        level,                     // new field for tri-state
      }
      this.wsSend(payload)
    }
  }

  // Send message with reply
  sendReply(content: string, replyToId: number, quotedText?: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      // Add optimistic message with reply info
      useChatStore.getState().addMessage({
        id: nextTempId(),
        role: 'user',
        content,
        mediaType: 'text',
        status: 'sending',
        replyToMessageId: replyToId,
        quotedText,
        createdAt: new Date().toISOString(),
      })

      this.wsSend({ type: 'message_with_reply', content, reply_to: replyToId, quoted_text: quotedText })

      // Clear reply state
      useChatStore.getState().setReplyingTo(null)
    }
  }

  // Send reaction
  sendReaction(messageId: number, emoji: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.wsSend({ type: 'reaction', message_id: messageId, emoji })
    }
  }

  // Edit message
  editMessage(messageId: number, newContent: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.wsSend({ type: 'edit_message', message_id: messageId, new_content: newContent })

      // Clear edit state
      useChatStore.getState().setEditingMessage(null)
    }
  }

  // Delete message
  deleteMessage(messageId: number) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.wsSend({ type: 'delete_message', message_id: messageId })
    }
  }

  // Clear chat
  clearChat(sessionId?: number) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.wsSend({ type: 'clear_chat', session_id: sessionId })
    }
  }

  // Interactive Consilium (Phase 1B)
  startConsilium() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.wsSend({ type: 'consilium_start' })
    }
  }

  sendConsiliumMessage(content: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.wsSend({ type: 'consilium_user_message', content })
    }
  }

  sendConsiliumResponse(action: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.wsSend({ type: 'consilium_user_response', action })
      // Dismiss prompt immediately on client side
      useChatStore.getState().setConsiliumPrompt(null)
    }
  }

  dismissConsilium() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.wsSend({ type: 'consilium_dismiss' })
    }
  }

  // Notify server that user has seen a message (for read receipts/analytics)
  sendMessageSeen(messageId: number) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.wsSend({ type: 'message_seen', message_id: messageId })
    }
  }

  // Event handlers
  setOnMessage(handler: MessageHandler) {
    this.onMessage = handler
  }

  setOnStatus(handler: StatusHandler) {
    this.onStatus = handler
  }

  setOnHandoff(handler: HandoffHandler) {
    this.onHandoff = handler
  }

  setOnError(handler: ErrorHandler) {
    this.onError = handler
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }
}

export const wsManager = new WebSocketManager()
